import React, { useEffect, useRef, useState, useCallback } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import IconButton from '@mui/material/IconButton';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import 'leaflet/dist/leaflet.css';
import 'leaflet.heat';

function HeatLayer({ positions = [] }) {
  const map = useMap();

  useEffect(() => {
    if (!map) return;

    const points = (positions || [])
      .map(({ lat, lng }) => [Number(lat), Number(lng), 0.7])
      .filter(([lat, lng]) => Number.isFinite(lat) && Number.isFinite(lng));

    const layer = L.heatLayer(points, {
      radius: 26,
      blur: 18,
      minOpacity: 0.2,
      maxZoom: 10,
      gradient: {
        0.35: '#7CFC00',
        0.55: '#FFD700',
        0.75: '#FF8C00',
        1.0: '#FF0000',
      },
    }).addTo(map);

    return () => {
      map.removeLayer(layer);
    };
  }, [map, positions]);

  return null;
}

function CenterController({ center, zoom }) {
  const map = useMap();

  useEffect(() => {
    const lat = Number(center?.lat);
    const lng = Number(center?.lng);

    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      map.setView([lat, lng], zoom);
    }
  }, [map, center, zoom]);

  return null;
}

function ResizeController({ trigger }) {
  const map = useMap();

  useEffect(() => {
    setTimeout(() => {
      map.invalidateSize();
    }, 80);
  }, [map, trigger]);

  return null;
}

function Mapa({
  height = '700px',
  zoom = 4,
  positions,
  center,
  children,
  ...rest
}) {
  const containerRef = useRef(null);
  const [fullScreen, setFullScreen] = useState(false);
  const defaultCenter = [Number(center?.lat) || -15.793889, Number(center?.lng) || -47.882778];

  useEffect(() => {
    const onFsChange = () => {
      const fsEl =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement;
      setFullScreen(Boolean(fsEl));
    };

    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    document.addEventListener('mozfullscreenchange', onFsChange);
    document.addEventListener('MSFullscreenChange', onFsChange);

    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
      document.removeEventListener('mozfullscreenchange', onFsChange);
      document.removeEventListener('MSFullscreenChange', onFsChange);
    };
  }, []);

  const toggleFullScreen = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;

    const enter =
      el.requestFullscreen ||
      el.webkitRequestFullscreen ||
      el.mozRequestFullScreen ||
      el.msRequestFullscreen;

    const exit =
      document.exitFullscreen ||
      document.webkitExitFullscreen ||
      document.mozCancelFullScreen ||
      document.msExitFullscreen;

    if (!fullScreen && enter) {
      enter.call(el);
    } else if (fullScreen && exit) {
      exit.call(document);
    }
  }, [fullScreen]);

  const mapHeight = fullScreen ? '100vh' : height;

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%', height: mapHeight }}>
      <IconButton
        onClick={toggleFullScreen}
        size="small"
        style={{
          position: 'absolute',
          top: 10,
          right: 10,
          zIndex: 1000,
          backgroundColor: '#fff',
          border: '1px solid #d9d9d9',
        }}
      >
        {fullScreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
      </IconButton>

      <MapContainer
        center={defaultCenter}
        zoom={zoom}
        style={{ position: 'relative', height: '100%', width: '100%' }}
        {...rest}
      >
        <TileLayer
          attribution='&copy; OpenStreetMap contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <CenterController center={center} zoom={zoom} />
        <ResizeController trigger={fullScreen} />
        <HeatLayer positions={positions} />
        {children}
      </MapContainer>
    </div>
  );
}

export default Mapa;